import React from "react";
import Icon from "components/Icon";
import { CommImageNameComp } from "custom_modules/Members/CommonComponents";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  TableText,
} from "styledComponents/common";

import avatar5 from "../../assets/img/avatar_5.svg";
import imageIcon from "../../assets/img/icons/Image.svg";
import Chat from "../../assets/img/icons/chat.svg";
import audioIcon from "../../assets/img/icons/play-icon.svg";
import taskIcon from "../../assets/img/icons/task.svg";
import tick from "../../assets/img/icons/tick-white-bold.svg";
import useAudioDurations from "../../hooks/useAudioDurations";
import {
  ClientLocationsTableHeaderContainer,
  ClientLocationsTableRowContainer,
  GeneralInfoItem,
  GeneralInfoSection,
  HoveredSection2,
  InnerDivTooltip,
  RoundHover,
  ToolTipContainer,
  TooltipText,
} from "../../styledComponents/clients";
import {
  CardBlock,
  GreenCircle,
  LateTag,
} from "../FieldServices/RouteMapNew/routeMapStyles";
import {
  CallSection,
  EmployeeInfoBlock,
  EvidenceIcon,
  EvidenceIconSection,
  EvidenceThumbnail,
  LabelTextBlock,
  NoteSection,
  StatusBar,
} from "./JobApproval/taskApprovalStyle";
import { getHoursWithSeconds, toHHMMSS } from "utils/helper";

const CollapseSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  padding: 5px;
  // border-top: 1px solid #c2cce1;
  border-right: 1px solid white;
  box-sizing: border-box;
  width: 100%;
  margin: 5px 10px 5px 10px;
  justify-content: flex-start;
  align-items: flex-start;
  background-color: " #B4C6F9";
`;

const JobListCollapseSection = ({
  task,
  orgTimezone,
  handleAssigneeEvidenceClick,
  toolTipChange,
  selectIndex,
  toolTip,
  isOperationType,
}) => {
  const { t } = useTranslation();
  const assignees = task.assignees || [];
  const subTasks = task.subtasks || [];
  // Extract audio URLs for the custom hook (support multiple evidences)
  const audioUrls =
    assignees?.reduce((urls, assignee) => {
      if (assignee?.evidences && Array.isArray(assignee.evidences)) {
        assignee.evidences.forEach(ev => {
          if (ev?.audio) urls.push(ev.audio);
        });
      } else if (assignee?.evidence?.audio) {
        urls.push(assignee.evidence.audio);
      }
      return urls;
    }, []) || [];
  // Use custom hook for audio durations
  const { getDuration } = useAudioDurations(audioUrls);
  return (
    <CollapseSection>
      {/* Clocked in section */}
      <CommonFlex
        direction="column"
        gap="10px"
        justifyContent="flex-start"
        alignItems="flex-start"
        style={{ borderRight: "1px solid white" }}
      >
        {assignees.map((assignee, index) => (
          <CommonGrid
            columns="250px 150px 150px 150px 1fr"
            borderBottom={
              index === assignees.length - 1 ? "transparent" : "white"
            }
            key={index}
            gap="20px"
            margin="0 10px 0 10px"
            padding="10px"
            style={{
              width: "100%",
            }}
          >
            <CommImageNameComp
              imgSource={assignee.avatar || avatar5}
              name={`${assignee.first_name} ${assignee.last_name}`}
              size="36px"
              fontSize="13px"
              index={index}
              color={assignee.color}
              hoverUnderline
              userId={assignee.id}
            />
            <CommonFlex
              gap="5px"
              direction="column"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <CommonText name title>
                {assignee.checkedin && assignee.checkedin.timestamp
                  ? moment.unix(assignee.checkedin.timestamp).format("h:mm a")
                  : "-"}
                {assignee.checkedin &&
                  assignee.checkedin.timestamp &&
                  (moment
                    .unix(assignee.checkedin.timestamp)
                    .isAfter(moment.utc(task.start_time).tz(orgTimezone)) ? (
                    <LateTag>{t("late")}</LateTag>
                  ) : (
                    <GreenCircle>
                      <img src={tick} alt="" width="100%" />
                    </GreenCircle>
                  ))}
              </CommonText>
              <TableText fontWeight="400">Checked In</TableText>
            </CommonFlex>
            <CommonFlex
              gap="5px"
              direction="column"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <CommonText name title>
                {assignee.checkedout && assignee.checkedout.timestamp
                  ? moment.unix(assignee.checkedout.timestamp).format("h:mm a")
                  : "-"}
                {assignee.checkedout &&
                  assignee.checkedout.timestamp &&
                  (moment
                    .unix(assignee.checkedout.timestamp)
                    .isAfter(moment.utc(task.end_time).tz(orgTimezone)) ? (
                    <LateTag>{t("late")}</LateTag>
                  ) : (
                    <GreenCircle>
                      <img src={tick} alt="" width="100%" />
                    </GreenCircle>
                  ))}
              </CommonText>
              <TableText fontWeight="400">Checked Out</TableText>
            </CommonFlex>
            <CommonFlex
              gap="5px"
              direction="column"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <CommonText name title>
                {assignee.checkedout && assignee.checkedout.timestamp
                  ? getHoursWithSeconds(toHHMMSS(assignee.checkedout.timestamp - assignee.checkedin.timestamp))
                  : "-"}
              </CommonText>
              <TableText fontWeight="400">Worked</TableText>
            </CommonFlex>
            <CommonFlex justifyContent="flex-start">
              <EvidenceIconSection>
                {(() => {
                  const allEvidences = assignee?.evidences || (assignee?.evidence ? [assignee.evidence] : []);
                  const visibleEvidences = allEvidences.slice(0, 4);
                  const hasMore = allEvidences.length > 4;
                  
                  return (
                    <>
                      {visibleEvidences.map((ev, evIndex) => (
                        <React.Fragment key={`assignee-${index}-evidence-${evIndex}`}>
                          {ev?.image && (
                            <EvidenceIcon
                              onClick={() => handleAssigneeEvidenceClick(assignee, ev.image)}
                            >
                              <CommonImage
                                src={ev.image}
                                alt=""
                              />
                            </EvidenceIcon>
                          )}
                          {ev?.audio && (
                            <EvidenceIcon
                              onClick={() => handleAssigneeEvidenceClick(assignee, ev.audio)}
                            >
                              <CommonImage src={audioIcon} alt="" size="25px" />
                              <span
                                style={{
                                  fontSize: "10px",
                                  color: "#2266EF",
                                  fontWeight: "600",
                                }}
                              >
                                {getDuration(ev.audio)}
                              </span>
                            </EvidenceIcon>
                          )}
                        </React.Fragment>
                      ))}
                      {hasMore && (
                        <EvidenceIcon
                          onClick={() => {
                            const fifthEvidence = allEvidences[4];
                            const evidenceSrc = fifthEvidence?.image || fifthEvidence?.audio;
                            if (evidenceSrc) {
                              handleAssigneeEvidenceClick(assignee, evidenceSrc);
                            }
                          }}
                          style={{
                            backgroundColor: '#2266EF',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          +{allEvidences.length - 4}
                        </EvidenceIcon>
                      )}
                      {allEvidences.length === 0 && (
                        <CommonText fontSize="12px">-</CommonText>
                      )}
                    </>
                  );
                })()}
              </EvidenceIconSection>
            </CommonFlex>
          </CommonGrid>
        ))}
      </CommonFlex>
      {/* subtask section */}
      <CommonFlex
        direction="column"
        gap="10px"
        justifyContent="flex-start"
        alignItems="flex-start"
        style={{ paddingLeft: "10px", minWidth: "200px" }}
      >
        <TableText fontWeight="400" fontSize="12px" margin="10px 0 0 0">
          {isOperationType ? "Subtask" : "LineItem"}
          {subTasks.length > 0 ? ` (${subTasks.length})` : ""}
        </TableText>
        {subTasks.length > 0 ? (
          <CommonFlex
            direction="column"
            gap="10px"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {subTasks.map((subTask, index) => (
              <CommonFlex
                key={index}
                direction="row"
                alignItems="center"
                gap="5px"
                justifyContent="flex-start"
              >
                <CommonImage src={taskIcon} alt="task" size="16px" />
                <TableText
                  margin="0px"
                  name
                  title
                  fontWeight="500"
                  fontSize="14px"
                >
                  {subTask.name}
                </TableText>
              </CommonFlex>
            ))}
          </CommonFlex>
        ) : (
          <TableText fontWeight="500" fontSize="14px" name margin="0px 0 0 0">
            No {isOperationType ? "subtask" : "line item"} found!
          </TableText>
        )}
      </CommonFlex>
    </CollapseSection>
  );
};

export default JobListCollapseSection;
